import { ControlledSleep } from "@/domains/event-loop/synchronization";
import { ExponentialBackoff } from "@/domains/network/ExponentialBackoff";
import { getBackendHealthCheckUrl } from "@/manager/backend-api-client/initializeBackendApiManager";
import { observable, runInAction } from "mobx";

const FORCE_CHECK_EVERY_MS = 30 * 1000;
const BACKOFF_MIN_MS = 250;
const BACKOFF_MAX_MS = 10 * 1000;
const MAX_NUM_SUBSEQUENT_FAILURES = 3;

export type NetworkStatus = {
  isOnline: boolean;
  lastOnline: Date;
};

export type NetworkInfo = {
  online: boolean | null;
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
};

const NetworkStatus: NetworkStatus = {
  isOnline: true,
  lastOnline: new Date(),
};

// Observable mirror of NetworkStatus.isOnline so reads via getIsOnline() inside a
// MobX reactive context (reaction/computed/autorun) track network transitions.
// Written in lockstep with NetworkStatus.isOnline in recordOnline/recordOffline.
const isOnlineBox = observable.box(NetworkStatus.isOnline);

type NetworkStatusSubscription = (networkStatus: NetworkStatus) => void | Promise<void>;

type NetworkDetectionContext = {
  backoff: ExponentialBackoff;
  controlledSleep: ControlledSleep;
  numSubsequentFailures: number;
  resolveWhenOnline?: () => void;
  pendingWhenOnline?: Promise<void>;
  subscriptions: NetworkStatusSubscription[];
};

const NetworkDetectionContext: NetworkDetectionContext = {
  backoff: new ExponentialBackoff(BACKOFF_MIN_MS, BACKOFF_MAX_MS),
  controlledSleep: new ControlledSleep(),
  numSubsequentFailures: 0,
  resolveWhenOnline: undefined,
  pendingWhenOnline: undefined,
  subscriptions: [],
};

export const isNetworkOk = async () => {
  try {
    const response = await fetch(getBackendHealthCheckUrl());
    const { success } = await response.json();
    return success == true && response.status >= 200 && response.status <= 299;
  } catch (_err) {
    // Fail gracefully so the request may be retried.
  }
  return false;
};

const notifySubscriptions = () => {
  NetworkDetectionContext.subscriptions.forEach(subscription => {
    subscription(NetworkStatus);
  });
};

const recordOffline = () => {
  const wasAlreadyOffline = !NetworkStatus.isOnline;
  if (wasAlreadyOffline) {
    return;
  }
  NetworkStatus.isOnline = false;
  runInAction(() => isOnlineBox.set(false));
  NetworkDetectionContext.pendingWhenOnline = new Promise(resolve => {
    NetworkDetectionContext.resolveWhenOnline = resolve;
  });
  notifySubscriptions();
};

const recordOnline = () => {
  const wasAlreadyOnline = NetworkStatus.isOnline;
  NetworkStatus.isOnline = true;
  NetworkStatus.lastOnline = new Date();
  NetworkDetectionContext.numSubsequentFailures = 0;
  if (NetworkDetectionContext.resolveWhenOnline) {
    NetworkDetectionContext.pendingWhenOnline = undefined;
    NetworkDetectionContext.resolveWhenOnline();
  }
  if (!wasAlreadyOnline) {
    runInAction(() => isOnlineBox.set(true));
    notifySubscriptions();
  }
};

const onlineDetectionLoop = async () => {
  for (;;) {
    const isOnline = await isNetworkOk();
    if (!isOnline) {
      recordNetworkFailure();
      await NetworkDetectionContext.controlledSleep.sleep(
        NetworkDetectionContext.backoff.getNextDelayMs()
      );
      continue;
    }
    // We're online as of now
    recordOnline();
    await NetworkDetectionContext.controlledSleep.sleep(FORCE_CHECK_EVERY_MS);
  }
};

const registerNetworkChangeEventHandlers = () => {
  const handleNetworkChange = () => {
    NetworkDetectionContext.backoff.reset();
    NetworkDetectionContext.controlledSleep.awake();
  };
  window.addEventListener("online", handleNetworkChange);
  window.addEventListener("offline", handleNetworkChange);
};

export const forceNetworkCheck = async () => {
  NetworkDetectionContext.controlledSleep.awake();
};

export const recordNetworkFailure = () => {
  NetworkDetectionContext.numSubsequentFailures++;
  if (NetworkDetectionContext.numSubsequentFailures === MAX_NUM_SUBSEQUENT_FAILURES) {
    recordOffline();
  }
};

export const getNetworkStatus = () => NetworkStatus;

export const getIsOnline = () => isOnlineBox.get();

export const getNetworkInfo = (): NetworkInfo => {
  if (typeof window === "undefined") {
    return { online: null };
  }
  const connection = typeof navigator === "undefined" ? undefined : navigator.connection;
  return {
    online: getIsOnline(),
    effectiveType: connection?.effectiveType,
    rtt: connection?.rtt,
    downlink: connection?.downlink,
    saveData: connection?.saveData,
  };
};

export const getWhenOnline = async () => {
  if (!NetworkDetectionContext.pendingWhenOnline) {
    return;
  }
  await NetworkDetectionContext.pendingWhenOnline;
};

export const subscribeToNetworkStatus = (subscription: NetworkStatusSubscription) => {
  NetworkDetectionContext.subscriptions.push(subscription);
  // Returns an unsubscribe() fn
  return () => {
    NetworkDetectionContext.subscriptions = NetworkDetectionContext.subscriptions.filter(
      existing => existing !== subscription
    );
  };
};

// Kick off the online detection loop.
registerNetworkChangeEventHandlers();
onlineDetectionLoop();
